import { DesktopIcon, MoonIcon, SunIcon } from '@radix-ui/react-icons';
import { IconButton, Tooltip } from '@radix-ui/themes';
import { useTranslation, type MessageKey } from '../i18n';
import { type ThemeMode, useThemeStore } from '../stores/themeStore';

const themeOptions: { mode: ThemeMode; labelKey: MessageKey; icon: typeof SunIcon }[] = [
  { mode: 'light', labelKey: 'theme.light', icon: SunIcon },
  { mode: 'dark', labelKey: 'theme.dark', icon: MoonIcon },
  { mode: 'system', labelKey: 'theme.system', icon: DesktopIcon },
];

interface ThemeToggleProps {
  variant?: 'icon' | 'menu';
  className?: string;
}

export function ThemeToggle({ variant = 'icon', className }: ThemeToggleProps) {
  const { t } = useTranslation();
  const appearance = useThemeStore((state) => state.appearance);
  const resolvedAppearance = useThemeStore((state) => state.resolvedAppearance);
  const setAppearance = useThemeStore((state) => state.setAppearance);
  const toggleAppearance = useThemeStore((state) => state.toggleAppearance);

  if (variant === 'menu') {
    return (
      <div className={className}>
        <div className="user-menu-section-label">{t('theme.label')}</div>
        <div className="theme-toggle-group" role="group" aria-label={t('theme.label')}>
          {themeOptions.map(({ mode, labelKey, icon: Icon }) => (
            <button
              key={mode}
              type="button"
              className={`theme-toggle-option${appearance === mode ? ' active' : ''}`}
              aria-pressed={appearance === mode}
              onClick={() => setAppearance(mode)}
            >
              <Icon aria-hidden="true" className="theme-toggle-option-icon" />
              <span>{t(labelKey)}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const ActiveIcon = resolvedAppearance === 'dark' ? MoonIcon : SunIcon;
  const nextLabel =
    resolvedAppearance === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark');

  return (
    <Tooltip content={nextLabel}>
      <IconButton
        type="button"
        variant="ghost"
        size="2"
        className={className}
        aria-label={nextLabel}
        onClick={toggleAppearance}
      >
        <ActiveIcon aria-hidden="true" />
      </IconButton>
    </Tooltip>
  );
}
